import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getClients, getGroups, getGroup, generateSummary, createSession, createClient, updateClient, transcribeStart, transcribeStatus, generateSessionPrep, createWin, getClientGroups, getOfferings } from '../api/client';
import OfferingFormModal from './OfferingFormModal';
import OfferingTypePicker from './OfferingTypePicker';
import ClientAddPicker from './ClientAddPicker';
import { useAuth } from '../context/AuthContext';

// Matches a line like "Speaker 1  0:00" or "Jane Doe  1:23:45" — the common
// Zoom/Otter transcript export format, where the speaker label and turn
// timestamp share one line. The timestamp's own colon would otherwise be
// misread as a "Name:" label by the colon-format detector below, so any line
// matching this pattern is treated as just its name for standalone-format
// purposes everywhere this is used (detection, sample lookup, relabeling).
const STANDALONE_TIMESTAMP_RE = /^(.{1,50}?)\s{1,4}\d{1,2}:\d{2}(?::\d{2})?$/;
const extractStandaloneName = (line) => {
  const m = line.match(STANDALONE_TIMESTAMP_RE);
  return m ? m[1].trim() : line;
};

// ── Speaker Panel — defined OUTSIDE NewSession to prevent re-mount on every keystroke ──
const SpeakerPanel = ({
  isTranscriptMode,
  speakerLabels,
  setSpeakerLabels,
  speakerAssignments,
  setSpeakerAssignments,
  speakerNames,
  setSpeakerNames,
  speakerFormat,
  transcript,
  setTranscript,
  fileTranscript,
  clients,
  setCreateClientSpeaker,
  setShowCreateClientModal,
  applyLabels,
  showToast,
  setSelectedClientIds,
}) => {
  // Removes a client from the session's selected-clients list, but only if no
  // OTHER speaker is still assigned to that same client — two speaker labels
  // can legitimately map to one client, and the client may also have been
  // added independently via the manual "who's in this session" picker.
  const maybeRemoveClient = (clientId, speakerBeingChanged) => {
    const stillReferenced = Object.entries(speakerAssignments).some(
      ([key, a]) => key !== speakerBeingChanged && a?.type === 'client' && a?.id === clientId
    );
    if (!stillReferenced) {
      setSelectedClientIds(prev => prev.filter(id => id !== clientId));
    }
  };

  const removeSpeaker = (speaker) => {
    const prevAssignment = speakerAssignments[speaker];
    if (prevAssignment?.type === 'client') maybeRemoveClient(prevAssignment.id, speaker);
    setSpeakerLabels(prev => prev.filter(s => s !== speaker));
    setSpeakerAssignments(prev => {
      const next = { ...prev };
      delete next[speaker];
      return next;
    });
    setSpeakerNames(prev => {
      const next = { ...prev };
      delete next[speaker];
      return next;
    });
  };

  const applyPasteLabels = () => {
    if (speakerFormat === 'standalone') {
      const lines = transcript.split('\n');
      const output = [];
      let i = 0;
      while (i < lines.length) {
        const line = lines[i].trim();
        const extractedName = extractStandaloneName(line);
        if (speakerLabels.includes(extractedName) && i + 1 < lines.length) {
          const assignment = speakerAssignments[extractedName];
          const name = speakerNames[extractedName] || assignment?.name || extractedName;
          output.push(`${name}: ${lines[i + 1].trim()}`);
          i += 2;
        } else {
          if (line) output.push(line);
          i++;
        }
      }
      setTranscript(output.join('\n\n'));
    } else {
      let result = transcript;
      speakerLabels.forEach(speaker => {
        const assignment = speakerAssignments[speaker];
        const name = speakerNames[speaker] || assignment?.name || speaker;
        result = result.replace(
          new RegExp(`^${speaker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:`, 'gm'),
          `${name}:`
        );
      });
      setTranscript(result);
    }
    showToast('Labels applied');
  };

  const coachCount = Object.values(speakerAssignments).filter(a => a?.type === 'therapist' || a?.type === 'facilitator' || (a?.type === 'client' && a?.facilitator)).length;

  return (
    <div style={spStyles.panel}>
      <div style={spStyles.title}>
        {speakerLabels.length} speaker{speakerLabels.length !== 1 ? 's' : ''} detected
      </div>
      <div style={spStyles.sub}>
        Assign each speaker so the AI knows who said what. If detection picked up a
        speaker who isn't real (e.g. a diarization glitch), use the × to remove them.
        {coachCount === 0 && (
          <span style={{ color: 'var(--red)', display: 'block', marginTop: '4px' }}>
            ⚠ Please mark which speaker is the coach.
          </span>
        )}
      </div>

      {speakerLabels.map(speaker => {
        const sampleSource = isTranscriptMode ? transcript : fileTranscript;
        // For standalone format, the speech is on the NEXT line after the name
        let sample = '';
        if (isTranscriptMode && speakerFormat === 'standalone') {
          const lines = sampleSource.split('\n');
          const idx = lines.findIndex(l => extractStandaloneName(l.trim()) === speaker);
          if (idx >= 0 && lines[idx + 1]) {
            sample = '"' + lines[idx + 1].trim().substring(0, 80) + '..."';
          }
        } else {
          const sampleLine = sampleSource.split('\n').find(l => l.startsWith(speaker + ':'));
          if (sampleLine) sample = '"' + sampleLine.replace(speaker + ':', '').trim().substring(0, 80) + '..."';
        }

        const currentVal =
          speakerAssignments[speaker]?.type === 'therapist' ? 'therapist' :
            speakerAssignments[speaker]?.type === 'facilitator' ? 'facilitator' :
              speakerAssignments[speaker]?.type === 'custom' ? 'custom' :
                speakerAssignments[speaker]?.id || '';

        return (
          <div key={speaker} style={spStyles.row}>
            <div style={{ minWidth: '100px', maxWidth: '180px' }}>
              <div style={spStyles.rowLabel}>{speaker}</div>
              {sample && <div style={spStyles.sample}>{sample}</div>}
            </div>
            <select
              style={spStyles.select}
              value={currentVal}
              onChange={e => {
                const val = e.target.value;
                const prevAssignment = speakerAssignments[speaker];
                if (val === 'therapist') {
                  if (prevAssignment?.type === 'client') maybeRemoveClient(prevAssignment.id, speaker);
                  setSpeakerAssignments(prev => ({ ...prev, [speaker]: { type: 'therapist', name: 'Coach' } }));
                } else if (val === 'facilitator') {
                  if (prevAssignment?.type === 'client') maybeRemoveClient(prevAssignment.id, speaker);
                  setSpeakerAssignments(prev => ({ ...prev, [speaker]: { type: 'facilitator', name: '' } }));
                } else if (val === 'custom') {
                  if (prevAssignment?.type === 'client') maybeRemoveClient(prevAssignment.id, speaker);
                  setSpeakerAssignments(prev => ({ ...prev, [speaker]: { type: 'custom', name: '' } }));
                } else if (val === 'new') {
                  const looksLikeName = !/^speaker\s+[a-z]$/i.test(speaker.trim());
                  setSpeakerNames(prev => ({ ...prev, [speaker]: looksLikeName ? speaker : '' }));
                  setCreateClientSpeaker(speaker);
                  setShowCreateClientModal(true);
                } else if (val === '') {
                  if (prevAssignment?.type === 'client') maybeRemoveClient(prevAssignment.id, speaker);
                  setSpeakerAssignments(prev => {
                    const next = { ...prev };
                    delete next[speaker];
                    return next;
                  });
                } else {
                  const client = clients.find(c => c.id === val);
                  if (prevAssignment?.type === 'client' && prevAssignment.id !== val) {
                    maybeRemoveClient(prevAssignment.id, speaker);
                  }
                  setSpeakerAssignments(prev => ({ ...prev, [speaker]: { type: 'client', id: val, name: client?.name || val } }));
                  setSelectedClientIds(prev => prev.includes(val) ? prev : [...prev, val]);
                }
              }}
            >
              <option value="">— Assign —</option>
              <option value="therapist">🎙 Coach (me)</option>
              <option value="facilitator">🎤 Facilitator (named)...</option>
              <optgroup label="Existing clients">
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
              <option value="new">+ Create new client...</option>
              <option value="custom">Custom name (no profile)...</option>
            </select>
            {(speakerAssignments[speaker]?.type === 'custom' || speakerAssignments[speaker]?.type === 'facilitator') && (
              <input
                style={{ ...spStyles.select, minWidth: '120px' }}
                placeholder={speakerAssignments[speaker]?.type === 'facilitator' ? "Facilitator name..." : "Enter name..."}
                value={speakerNames[speaker] || ''}
                onChange={e => {
                  const val = e.target.value;
                  setSpeakerNames(prev => ({ ...prev, [speaker]: val }));
                  setSpeakerAssignments(prev => ({ ...prev, [speaker]: { ...prev[speaker], name: val } }));
                }}
              />
            )}
            {speakerAssignments[speaker]?.type === 'client' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: 'var(--text-soft)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={speakerAssignments[speaker]?.facilitator === true}
                  onChange={e => setSpeakerAssignments(prev => ({ ...prev, [speaker]: { ...prev[speaker], facilitator: e.target.checked } }))}
                />
                Facilitator
              </label>
            )}
            <button
              type="button"
              title="Not a real speaker — remove"
              style={spStyles.removeBtn}
              onClick={() => removeSpeaker(speaker)}
            >
              ×
            </button>
          </div>
        );
      })}

      <button
        style={spStyles.applyBtn}
        onClick={isTranscriptMode ? applyPasteLabels : applyLabels}
      >
        Apply labels to transcript
      </button>
    </div>
  );
};

const spStyles = {
  panel: { background: 'var(--gold-pale)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '10px', padding: '16px', marginTop: '16px' },
  title: { fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '4px' },
  sub: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '12px' },
  row: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 0', borderBottom: '1px solid rgba(201,168,76,0.2)', flexWrap: 'wrap' },
  rowLabel: { fontSize: '13px', fontWeight: '600', color: 'var(--text-dark)' },
  sample: { fontSize: '11px', color: 'var(--text-faint)', fontStyle: 'italic', marginTop: '3px', lineHeight: 1.4 },
  select: { flex: 1, padding: '8px 12px', borderRadius: '7px', border: '1px solid var(--border)', background: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: 'var(--text-dark)', outline: 'none' },
  removeBtn: { width: '26px', height: '26px', borderRadius: '50%', border: '1px solid var(--border)', background: 'white', color: 'var(--text-soft)', fontSize: '15px', lineHeight: 1, cursor: 'pointer', flexShrink: 0 },
  applyBtn: { marginTop: '12px', padding: '10px 18px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500' },
};

// ── Main component ──
// `lockSelection`: hides the client/group picker and fixes the session to
// `initialClientIds`/`initialGroupId` — used when embedded on a Client or
// Group page, where it's already obvious who the session is for.
// `embedded`: drops the standalone page chrome (title/padding) to fit inside
// a parent card. `initialCapture` ({type:'file', file} | {type:'record'}) is
// consumed once on mount, so a host page's own quick-capture card (e.g. the
// Sessions tab on ClientProfile) can hand off straight into an engaged capture. `onSaved`/`onCancel` let the host page control what happens
// next instead of the default standalone navigation.
export default function SessionForm({
  initialClientIds = [],
  initialGroupId = '',
  lockSelection = false,
  lockedLabel = '',
  embedded = false,
  initialCapture = null,
  onSaved,
  onCancel,
}) {
  const navigate = useNavigate();
  const { profile } = useAuth();

  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId || '');
  const [manualTitle, setManualTitle] = useState('');
  const [clientGroupOptions, setClientGroupOptions] = useState([]);
  const [groupContextId, setGroupContextId] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState(initialClientIds);
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);
  const [postReflection, setPostReflection] = useState('');
  const [highlightedPrepItems, setHighlightedPrepItems] = useState({});
  const [prepItemNotes, setPrepItemNotes] = useState({});
  const [inputMode, setInputMode] = useState('live');
  // Which capture path the coach has engaged, if any — null shows the
  // hero-drop-zone / Start-recording / Add-notes chooser; set once any path
  // is entered so the chosen panel (keyed off inputMode) takes over. Separate
  // from inputMode itself so the chooser doesn't briefly reappear mid-async
  // (e.g. the gap between clicking "Start recording" and getUserMedia resolving).
  const [capturePath, setCapturePath] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [fileTranscript, setFileTranscript] = useState('');
  const [speakersExpected, setSpeakersExpected] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcribingStatus, setTranscribingStatus] = useState('');
  const [speakerLabels, setSpeakerLabels] = useState([]);
  const [speakerAssignments, setSpeakerAssignments] = useState({});
  const [speakerNames, setSpeakerNames] = useState({});
  const [speakerFormat, setSpeakerFormat] = useState('colon');
  const [rawUtterances, setRawUtterances] = useState([]);
  const [starredQuotes, setStarredQuotes] = useState([]);
  const [dismissedQuotes, setDismissedQuotes] = useState([]);
  const [manualQuote, setManualQuote] = useState('');
  const [manualQuotes, setManualQuotes] = useState([]);
  const [suggestedGoalStatuses, setSuggestedGoalStatuses] = useState({});
  const [editingGoalTexts, setEditingGoalTexts] = useState({});
  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [createClientSpeaker, setCreateClientSpeaker] = useState(null);
  const [newClientForm, setNewClientForm] = useState({ name: '', session_type: 'Life coaching' });
  const [creatingClient, setCreatingClient] = useState(false);
  const [prepData, setPrepData] = useState(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const [prepOpen, setPrepOpen] = useState(true);
  const [showWinPrompt, setShowWinPrompt] = useState(false);
  const [winText, setWinText] = useState('');
  const [savingWin, setSavingWin] = useState(false);

  // No visible picker anymore — set silently from the chosen Offering's
  // modalities so the AI summary still gets that context.
  const [sessionModalities, setSessionModalities] = useState([]);
  const [sessionModalityNotes, setSessionModalityNotes] = useState('');

  const [offerings, setOfferings] = useState([]);
  const [selectedOfferingId, setSelectedOfferingId] = useState('');
  const [showOfferingModal, setShowOfferingModal] = useState(false);

  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    fetchClients(); fetchGroups();
    getOfferings().then(res => setOfferings(res.data || [])).catch(() => setOfferings([]));
  }, []);

  useEffect(() => {
    setGroupContextId('');
    if (selectedGroupId || selectedClientIds.length !== 1) { setClientGroupOptions([]); return; }
    getClientGroups(selectedClientIds[0])
      .then(res => setClientGroupOptions(res.data || []))
      .catch(() => setClientGroupOptions([]));
  }, [selectedGroupId, selectedClientIds]);

  const fetchClients = async () => {
    try {
      const res = await getClients();
      setClients(res.data);
    } catch (err) {
      setError('Could not load clients.');
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await getGroups();
      setGroups(res.data || []);
    } catch {}
  };

  // Uses the offering's own modalities as this session's AI context — shared
  // by picking an existing offering and by finishing the "+ New offering type"
  // modal, which only has the just-created offering object in hand, not yet
  // reflected in `offerings` state.
  const applyOfferingModalities = (offering) => {
    setSessionModalities(offering.modalities || []);
  };

  const handleOfferingSelect = (offeringId) => {
    setSelectedOfferingId(offeringId);
    const offering = offerings.find(o => o.id === offeringId);
    if (offering) applyOfferingModalities(offering);
  };

  // Created inline via "+ New offering type" — drop straight back into the
  // session with it selected, same as picking an existing one from the list.
  const handleOfferingCreatedInline = (newOffering) => {
    setOfferings(prev => [...prev, newOffering]);
    setShowOfferingModal(false);
    setSelectedOfferingId(newOffering.id);
    applyOfferingModalities(newOffering);
  };

  // Swaps out the previous group's auto-selected members (keeping any clients
  // the coach added by hand), so changing the group — or going back to
  // "No group" — actually updates the client list instead of leaving the
  // first group's members behind. groupRequestRef drops a stale response if
  // the coach switches again before an earlier getGroup() resolves.
  const groupMemberIdsRef = useRef([]);
  const groupRequestRef = useRef(0);
  const applyGroupMembers = (memberIds) => {
    const previous = groupMemberIdsRef.current;
    groupMemberIdsRef.current = memberIds;
    setSelectedClientIds(prev => {
      const kept = prev.filter(id => !previous.includes(id));
      const next = [...kept, ...memberIds.filter(id => !kept.includes(id))];
      setSpeakersExpected(next.length > 0 ? next.length + 1 : '');
      return next;
    });
  };

  const handleGroupSelect = async (groupId) => {
    setSelectedGroupId(groupId);
    const requestId = ++groupRequestRef.current;
    applyGroupMembers([]);
    if (!groupId) return;
    try {
      const res = await getGroup(groupId);
      if (requestId !== groupRequestRef.current) return;
      applyGroupMembers((res.data.clients || []).map(c => c.id));
    } catch {}
  };

  const toggleClient = (clientId) => {
    setSelectedClientIds(prev => {
      const next = prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId];
      setSpeakersExpected(next.length > 0 ? next.length + 1 : '');
      return next;
    });
  };

  const selectedClients = clients.filter(c => selectedClientIds.includes(c.id));

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const showToast = useCallback((msg) => {
    const el = document.createElement('div');
    el.textContent = msg;
    el.style.cssText = 'position:fixed;bottom:24px;right:24px;background:var(--forest);color:white;padding:12px 20px;border-radius:10px;font-size:13.5px;z-index:9999;font-family:DM Sans,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,0.2)';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2500);
  }, []);

  const finish = () => {
    if (onSaved) onSaved();
    else navigate('/clients');
  };

  // Shared by every drop/browse entry point. `textFallback` treats any
  // non-audio file as a transcript (the file picker's accept list already
  // narrows it); drag-and-drop only accepts recognizable text types.
  const handleIncomingFile = (file, { textFallback = false } = {}) => {
    if (!file) return;
    if (file.type.startsWith('audio/')) {
      setInputMode('file');
      setAudioFile(file);
      setCapturePath('file');
    } else if (textFallback || file.type.startsWith('text/') || /\.(txt|md|vtt|srt)$/i.test(file.name)) {
      const reader = new FileReader();
      reader.onload = ev => { setInputMode('transcript'); setTranscript(ev.target.result); };
      reader.readAsText(file);
      setCapturePath('notes');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4']
        .find(t => MediaRecorder.isTypeSupported(t)) || 'audio/webm';
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioUrl(URL.createObjectURL(blob));
        setRecordingComplete(true);
        stream.getTracks().forEach(t => t.stop());
        await transcribeFile(new File([blob], `recording_${Date.now()}.webm`, { type: mimeType }));
      };
      mediaRecorderRef.current.start(1000);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
      setRecording(true);
      setPaused(false);
    } catch (err) {
      setError('Microphone access denied.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    setPaused(false);
  };

  const togglePause = () => {
    if (!mediaRecorderRef.current) return;
    if (paused) {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
      setPaused(false);
    } else {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      setPaused(true);
    }
  };

  const transcribeFile = async (file) => {
    setTranscribing(true);
    setTranscribingStatus('Uploading audio...');
    setSpeakerLabels([]); setSpeakerAssignments({}); setSpeakerNames({});
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (speakersExpected) formData.append('speakers_expected', speakersExpected);
      const startRes = await transcribeStart(formData);
      const { transcript_id } = startRes.data;
      setTranscribingStatus('Detecting speakers...');
      let result;
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        const pollRes = await transcribeStatus(transcript_id);
        result = pollRes.data;
        if (result.status === 'completed') break;
        if (result.status === 'error') throw new Error(result.error);
      }
      const utterances = result.utterances || [];
      setRawUtterances(utterances);
      const speakers = [...new Set(utterances.map(u => `Speaker ${u.speaker}`))];
      setSpeakerLabels(speakers);
      setSpeakerFormat('colon');
      const initialAssignments = {};
      speakers.forEach((speaker, i) => {
        if (i === 0) initialAssignments[speaker] = { type: 'therapist', name: 'Coach' };
        else if (selectedClients[i - 1]) initialAssignments[speaker] = { type: 'client', id: selectedClients[i - 1].id, name: selectedClients[i - 1].name };
        else initialAssignments[speaker] = { type: 'custom', name: speaker };
      });
      setSpeakerAssignments(initialAssignments);
      setFileTranscript(utterances.map(u => `Speaker ${u.speaker}: ${u.text}`).join('\n\n'));
      showToast(`Transcription complete — ${speakers.length} speakers detected`);
    } catch (err) {
      setError('Transcription failed: ' + err.message);
    } finally {
      setTranscribing(false);
      setTranscribingStatus('');
    }
  };

  const applyLabels = () => {
    const formatted = rawUtterances.map(u => {
      const speakerKey = `Speaker ${u.speaker}`;
      const assignment = speakerAssignments[speakerKey];
      const name = speakerNames[speakerKey] || assignment?.name || speakerKey;
      return `${name}: ${u.text}`;
    }).join('\n\n');
    setFileTranscript(formatted);
    showToast('Labels applied');
  };

  const detectSpeakers = () => {
    const lines = transcript.split('\n').map(l => l.trim()).filter(Boolean);

    const notNames = new Set([
      'from', 'to', 'subject', 'date', 'time', 'note', 're', 'cc',
      'that', 'this', 'it', "it's", 'you', 'your', 'shall', 'also', 'and', 'but',
      'so', 'well', 'yes', 'no', 'ok', 'okay', 'hey', 'hi', 'hello', 'thanks',
      'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    ]);

    const isValidLabel = (s) =>
      s.length >= 2 &&
      s.length <= 50 &&
      !notNames.has(s.toLowerCase()) &&
      !/^\d/.test(s);

    // Format 1: "Name  H:MM" or "Name  H:MM:SS" alone on its own line — the
    // Zoom/Otter transcript export format. Checked first, and excluded from
    // the other two formats below, since the timestamp's own colon would
    // otherwise be misread by the colon-format detector as a new "Name:"
    // label every time the minute/second changes.
    const timestampCounts = {};
    const timestampDisplay = {};
    lines.forEach(line => {
      const m = line.match(STANDALONE_TIMESTAMP_RE);
      if (!m) return;
      const name = m[1].trim();
      if (!isValidLabel(name)) return;
      const key = name.toLowerCase().replace(/\s+/g, ' ');
      timestampCounts[key] = (timestampCounts[key] || 0) + 1;
      if (!timestampDisplay[key]) timestampDisplay[key] = name;
    });
    const timestampFormat = Object.entries(timestampCounts)
      .filter(([, count]) => count >= 2)
      .map(([key]) => timestampDisplay[key]);

    // Format 2: "Name: speech" on same line
    // Only count candidates that appear 2+ times (real speakers repeat) — otherwise
    // a one-off line like "Note: bring your notebook" gets counted as a new speaker.
    // Counting is normalized on case/whitespace so "Coach:" and "COACH:" collapse to
    // one speaker instead of being treated as two distinct labels.
    const colonCandidateCounts = {};
    const colonCandidateDisplay = {};
    lines.forEach(line => {
      if (STANDALONE_TIMESTAMP_RE.test(line)) return;
      const m = line.match(/^([^\n:]{1,50}):/);
      if (!m) return;
      const s = m[1].trim();
      if (!isValidLabel(s)) return;
      const key = s.toLowerCase().replace(/\s+/g, ' ');
      colonCandidateCounts[key] = (colonCandidateCounts[key] || 0) + 1;
      if (!colonCandidateDisplay[key]) colonCandidateDisplay[key] = s;
    });
    const colonFormat = Object.entries(colonCandidateCounts)
      .filter(([, count]) => count >= 2)
      .map(([key]) => colonCandidateDisplay[key]);

    // Format 3: Name alone on its own line (no timestamp), speech on the next line
    // Only count candidates that appear 2+ times (real speakers repeat)
    const candidateCounts = {};
    const candidateDisplay = {};
    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i].trim();
      const nextLine = lines[i + 1].trim();
      if (STANDALONE_TIMESTAMP_RE.test(line)) continue;
      const looksLikeName = (
        isValidLabel(line) &&
        !line.endsWith('.') &&
        !line.endsWith('?') &&
        !line.endsWith('!') &&
        !line.endsWith(',') &&
        !line.endsWith(':') &&
        nextLine.length > 15
      );
      if (looksLikeName) {
        const key = line.toLowerCase().replace(/\s+/g, ' ');
        candidateCounts[key] = (candidateCounts[key] || 0) + 1;
        if (!candidateDisplay[key]) candidateDisplay[key] = line;
      }
    }
    const standaloneFormat = Object.entries(candidateCounts)
      .filter(([, count]) => count >= 2)
      .map(([key]) => candidateDisplay[key]);

    // Timestamp-label format wins outright when present — it's the most specific
    // pattern and the one most likely to produce false positives if left to compete
    // by count alone. Otherwise pick whichever of the other two found more speakers.
    let useStandalone = standaloneFormat.length > colonFormat.length;
    let finalDetected = useStandalone ? standaloneFormat : colonFormat;
    if (timestampFormat.length > 0) {
      useStandalone = true;
      finalDetected = timestampFormat;
    }
    finalDetected = [...new Set(finalDetected)];

    if (finalDetected.length === 0) {
      showToast('No speakers found. Make sure names appear consistently.');
      return;
    }

    setSpeakerFormat(useStandalone ? 'standalone' : 'colon');
    setSpeakerLabels(finalDetected);

    const initial = {};
    finalDetected.forEach((speaker, i) => {
      // Try exact name match first
      const exactMatch = clients.find(c => c.name.toLowerCase() === speaker.toLowerCase());
      // Then try first name match
      const firstNameMatch = !exactMatch && clients.find(c =>
        c.name.toLowerCase().split(' ')[0] === speaker.toLowerCase().split(' ')[0]
      );
      const matchedClient = exactMatch || firstNameMatch;

      if (matchedClient) {
        initial[speaker] = { type: 'client', id: matchedClient.id, name: matchedClient.name };
        setSelectedClientIds(prev => prev.includes(matchedClient.id) ? prev : [...prev, matchedClient.id]);
      } else {
        // Default unrecognized speakers to custom/no profile — coach must be assigned manually
        initial[speaker] = { type: 'custom', name: speaker };
      }
    });
    setSpeakerAssignments(initial);
    showToast(`${finalDetected.length} speaker${finalDetected.length !== 1 ? 's' : ''} detected — please assign roles below`);
  };

  const getCurrentTranscript = () => {
    if (inputMode === 'file' || inputMode === 'live') return fileTranscript;
    return transcript;
  };

  const handleGenerateSummary = async () => {
    if (selectedClientIds.length === 0) { setError('Please select at least one client.'); return; }
    const content = getCurrentTranscript();
    if (!content.trim()) { setError('Please add session content first.'); return; }
    setGenerating(true);
    setError('');
    try {
      const primaryClientId = selectedClientIds[0];
      const client = clients.find(c => c.id === primaryClientId);

      const speaker_map = {};
      Object.entries(speakerAssignments).forEach(([speaker, assignment]) => {
        if (assignment?.type === 'therapist' || assignment?.type === 'facilitator') speaker_map[speaker] = 'coach';
        else if (assignment?.type === 'client' && assignment?.facilitator) speaker_map[speaker] = 'coach';
        else if (assignment?.type === 'client') speaker_map[speaker] = assignment.id;
        else if (assignment?.type === 'custom') speaker_map[speaker] = 'unknown';
      });

      const coachLabel = Object.entries(speakerAssignments)
        .find(([, a]) => a?.type === 'therapist' || a?.type === 'facilitator' || (a?.type === 'client' && a?.facilitator))?.[0] || null;

      const res = await generateSummary({
        client_id: primaryClientId,
        client_ids: selectedClientIds,
        date: sessionDate,
        transcript: content,
        modality: client?.modality,
        // Always send the real array (even empty) so the backend can tell "coach
        // picked nothing for this session" apart from "field wasn't sent at all" —
        // an explicit [] must NOT fall back to re-applying the coach's whole
        // Settings menu.
        modalities: sessionModalities,
        custom_modality_notes: sessionModalityNotes || undefined,
        speaker_map: Object.keys(speaker_map).length > 0 ? speaker_map : {},
        coach_name_in_transcript: coachLabel,
        group_context_name: groupContextId ? clientGroupOptions.find(g => g.id === groupContextId)?.name : undefined,
      });

      setSummary(res.data);
    } catch (err) {
      setError('Could not generate summary: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (selectedClientIds.length === 0 || !summary) return;
    if (!selectedOfferingId) { setError('Please select an Offering before saving.'); return; }
    setSaving(true);
    try {
      const acceptedGoals = (summary?.suggestedGoals || [])
        .filter((_, i) => suggestedGoalStatuses[i] === 'accepted' || suggestedGoalStatuses[i] === 'editing')
        .map((sg, i) => ({ text: editingGoalTexts[i] || sg.text, status: 'active', source: 'ai_suggested' }));

      const breakthroughMoments = [
        ...(summary?.breakthroughQuotes || [])
          .filter((_, i) => !dismissedQuotes.includes(i))
          .map((bq, i) => ({ ...bq, starred: starredQuotes.includes(i), manual: false })),
        ...manualQuotes.map(mq => ({ ...mq, starred: true }))
      ];

      const speaker_map = {};
      Object.entries(speakerAssignments).forEach(([speaker, assignment]) => {
        if (assignment?.type === 'therapist' || assignment?.type === 'facilitator') speaker_map[speaker] = 'coach';
        else if (assignment?.type === 'client' && assignment?.facilitator) speaker_map[speaker] = 'coach';
        else if (assignment?.type === 'client') speaker_map[speaker] = assignment.id;
        else if (assignment?.type === 'custom') speaker_map[speaker] = 'unknown';
      });

      const groupName = groups.find(g => g.id === selectedGroupId)?.name || 'Group';
      const formattedSessionDate = new Date(sessionDate).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });
      let sessionTitle;
      if (selectedGroupId) {
        sessionTitle = manualTitle.trim() ? `${groupName} - ${manualTitle.trim()}` : `${groupName} Session ${formattedSessionDate}`;
      } else if (manualTitle.trim()) {
        sessionTitle = manualTitle.trim();
      } else {
        sessionTitle = summary.title;
      }

      const sessionData = {
        date: sessionDate,
        title: sessionTitle,
        post_reflection: postReflection,
        input_mode: inputMode,
        transcript: getCurrentTranscript(),
        summary,
        client_ids: selectedClientIds,
        group_id: selectedGroupId || null,
        group_context_id: groupContextId || null,
        speaker_map: Object.keys(speaker_map).length > 0 ? speaker_map : {},
        session_type: selectedClientIds.length > 1 ? 'group' : 'individual',
        participant_names: selectedClients.map(c => c.name),
        breakthrough_quotes: breakthroughMoments,
        suggested_goals: acceptedGoals,
        session_modalities: sessionModalities && sessionModalities.length ? sessionModalities : null,
        session_modality_notes: sessionModalityNotes || null,
        offering_id: selectedOfferingId || null,
      };

      await createSession(selectedClientIds[0], { ...sessionData, client_id: selectedClientIds[0] });

      // Append accepted goals to each client's long_goals
      if (acceptedGoals.length > 0) {
        await Promise.all(selectedClients.map(async (c) => {
          try {
            const clientRes = await getClients();
            const freshClient = clientRes.data.find(cl => cl.id === c.id);
            const existingGoals = freshClient?.long_goals || [];
            const newGoals = acceptedGoals.map(g => ({
              text: g.text,
              status: 'active',
              added: new Date().toISOString(),
              source: 'ai_suggested'
            }));
            await updateClient(c.id, { ...freshClient, long_goals: [...existingGoals, ...newGoals] });
          } catch (err) {
            console.error(`Could not update long goals for client ${c.id}:`, err);
          }
        }));
      }

      showToast('Session saved');
      setShowWinPrompt(true);
    } catch (err) {
      setError('Could not save session: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateClient = async () => {
    const name = (newClientForm.name || (createClientSpeaker ? (speakerNames[createClientSpeaker] || createClientSpeaker) : '')).trim();
    if (!name) return;
    setCreatingClient(true);
    try {
      const res = await createClient({ name, session_type: newClientForm.session_type, notes: '', intake_background: '' });
      const newClient = res.data;
      setClients(prev => [...prev, newClient]);
      setSelectedClientIds(prev => prev.includes(newClient.id) ? prev : [...prev, newClient.id]);
      if (createClientSpeaker) {
        setSpeakerAssignments(prev => ({ ...prev, [createClientSpeaker]: { type: 'client', id: newClient.id, name: newClient.name } }));
      }
      setShowCreateClientModal(false);
      setNewClientForm({ name: '', session_type: 'Life coaching' });
      showToast(`${name} added as a client`);
    } catch (err) {
      setError('Could not create client.');
    } finally {
      setCreatingClient(false);
    }
  };

  const speakerPanelProps = {
    speakerLabels,
    setSpeakerLabels,
    speakerAssignments,
    setSpeakerAssignments,
    speakerNames,
    setSpeakerNames,
    speakerFormat,
    transcript,
    setTranscript,
    fileTranscript,
    clients,
    setCreateClientSpeaker,
    setShowCreateClientModal,
    applyLabels,
    showToast,
    setSelectedClientIds,
  };

  useEffect(() => {
    if (!initialCapture) return;
    if (initialCapture.type === 'file') handleIncomingFile(initialCapture.file, { textFallback: true });
    else if (initialCapture.type === 'record') { setInputMode('live'); setCapturePath('live'); startRecording(); }
    // Mount-only: the host remounts SessionForm for each new capture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={embedded ? styles.embeddedPage : styles.page}>
      {!embedded && (
        <div style={styles.header}>
          <div style={styles.pageTitle}>New Session</div>
          <div style={styles.pageSubtitle}>Record, transcribe, and summarize a coaching session</div>
        </div>
      )}

      {onCancel && (
        <button style={styles.embeddedCancelBtn} onClick={onCancel}>← Cancel</button>
      )}

      {error && (
        <div style={styles.errorAlert}>
          {error}
          <button onClick={() => setError('')} style={styles.errorClose}>×</button>
        </div>
      )}

      {/* ── Step 1: Capture Session Content ── */}
      <div
        style={{ ...styles.card, ...(isDraggingOver ? { borderColor: 'var(--sage)', backgroundColor: 'var(--mint)', transition: 'all 0.15s' } : {}) }}
        onDragOver={e => { e.preventDefault(); setIsDraggingOver(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOver(false); }}
        onDrop={e => {
          e.preventDefault();
          setIsDraggingOver(false);
          handleIncomingFile(e.dataTransfer.files[0]);
        }}
      >
        <div style={styles.cardHeader}>
          <div style={styles.stepBadge}>1</div>
          <span style={styles.cardTitle}>Session details</span>
        </div>
        <div style={styles.cardBody}>
          <div style={styles.formRow}>
            <div style={{ ...styles.formGroup, flex: 2 }}>
              <label style={styles.label}>Session title (optional)</label>
              <input
                style={styles.input}
                value={manualTitle}
                onChange={e => setManualTitle(e.target.value)}
                placeholder="Leave blank to keep the AI-generated title"
              />
            </div>
            <div style={{ ...styles.formGroup, flex: 1 }}>
              <label style={styles.label}>Session date</label>
              <input type="date" style={styles.input} value={sessionDate} onChange={e => setSessionDate(e.target.value)} />
            </div>
          </div>

          {lockSelection ? (
            <div style={styles.selectedBar}>
              <div style={styles.selectedBarLabel}>Session for</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {lockedLabel ? (
                  <span style={styles.clientTag}>{lockedLabel}</span>
                ) : (
                  selectedClients.map(c => <span key={c.id} style={styles.clientTag}>{c.name}</span>)
                )}
              </div>
            </div>
          ) : (
            <>
              {groups.length > 0 && profile?.session_focus !== 'individual' && (
                <div style={{ ...styles.formGroup, marginBottom: '12px' }}>
                  <label style={styles.label}>Group (optional)</label>
                  <select
                    style={styles.input}
                    value={selectedGroupId}
                    onChange={e => handleGroupSelect(e.target.value)}
                  >
                    <option value="">— No group —</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                  {selectedGroupId && (
                    <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '4px' }}>
                      Group members auto-selected below — adjust if needed
                    </div>
                  )}
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Clients</label>
                {selectedClients.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginBottom: '8px' }}>
                    Picked up from speaker assignment below — adjust above if needed.
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {selectedClients.map(c => (
                    <span key={c.id} style={styles.clientTag}>
                      {c.name}
                      <span style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => toggleClient(c.id)}>×</span>
                    </span>
                  ))}
                  <ClientAddPicker
                    clients={clients}
                    selectedClientIds={selectedClientIds}
                    onToggle={toggleClient}
                    onCreateNew={() => {
                      setCreateClientSpeaker(null);
                      setNewClientForm({ name: '', session_type: 'Life coaching' });
                      setShowCreateClientModal(true);
                    }}
                  />
                </div>
                {clients.length === 0 && (
                  <p style={styles.hint}>No clients yet — add one above.</p>
                )}
              </div>
            </>
          )}

          <OfferingTypePicker
            offerings={offerings}
            selectedOfferingId={selectedOfferingId}
            onSelect={handleOfferingSelect}
            onCreateNew={() => setShowOfferingModal(true)}
          />
          {offerings.length === 0 && (
            <p style={{ ...styles.hint, marginTop: '-12px', marginBottom: '16px' }}>
              No offerings yet — create one above, or <Link to="/settings" style={styles.link}>manage them in Settings →</Link>
            </p>
          )}

          {capturePath === null ? (
            <>
              {/* Hero drop zone — ~90% of real usage per the founder's spec notes */}
              <div
                style={styles.uploadZone}
                onClick={() => document.getElementById('audioFileInput').click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  // The whole card also accepts drops — stop here so the file
                  // isn't handled twice, and clear the card's drag highlight.
                  e.stopPropagation();
                  setIsDraggingOver(false);
                  handleIncomingFile(e.dataTransfer.files[0]);
                }}
              >
                <div style={styles.uploadIcon}>☁️</div>
                <div style={styles.uploadText}>Drag &amp; drop a recording or transcript</div>
                <div style={styles.uploadSub}>or <span style={styles.uploadLink}>click to browse</span></div>
                <div style={styles.uploadHint}>Audio or transcript files</div>
                <input
                  type="file"
                  id="audioFileInput"
                  accept="audio/*,.txt,.md,.vtt,.srt,text/*"
                  style={{ display: 'none' }}
                  onChange={e => handleIncomingFile(e.target.files[0], { textFallback: true })}
                />
              </div>

              <div style={styles.orDivider}><span style={styles.orDividerText}>or, live right now</span></div>

              <div style={styles.liveNowRow} onClick={() => { setInputMode('live'); setCapturePath('live'); startRecording(); }}>
                <span style={styles.liveNowDot} />
                <div style={{ flex: 1 }}>
                  <div style={styles.liveNowTitle}>Start recording</div>
                  <div style={styles.liveNowSub}>Capture a live call — you can tag it after</div>
                </div>
                <span style={styles.liveNowMic}>🎙</span>
              </div>

              <button
                type="button"
                style={styles.notesInsteadLink}
                onClick={() => { setInputMode('notes'); setCapturePath('notes'); }}
              >
                📝 Add notes instead
              </button>
            </>
          ) : (
          <>
          {/* Live recording */}
          {inputMode === 'live' && (
            <div style={{ ...styles.recordPanel, ...(recording ? styles.recordPanelActive : {}) }}>
              <div style={{ ...styles.recordTimer, color: recording ? 'var(--red)' : 'var(--text-dark)' }}>
                {formatTime(recordingTime)}
              </div>
              <div style={styles.recordControls}>
                {!recording ? (
                  <button style={styles.recordBtn} onClick={startRecording}>● Start recording</button>
                ) : (
                  <>
                    <button style={styles.stopBtn} onClick={stopRecording}>■ Stop</button>
                    <button style={styles.pauseBtn} onClick={togglePause}>{paused ? '▶ Resume' : '⏸ Pause'}</button>
                  </>
                )}
              </div>
              {!recordingComplete && !transcribing && (
                <div style={styles.recordHint}>{recording ? 'Recording in progress...' : 'Press start to begin'}</div>
              )}
              {transcribing && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <div style={styles.progressBar}><div style={styles.progressFill} /></div>
                  <div style={styles.transcribingStatus}>{transcribingStatus}</div>
                </div>
              )}
              {recordingComplete && !transcribing && (
                <div style={{ marginTop: '16px' }}>
                  <div style={styles.successAlert}>Recording complete — {formatTime(recordingTime)} captured</div>
                  <audio controls src={audioUrl} style={styles.audioPlayer} />
                </div>
              )}
              {speakerLabels.length > 0 && !transcribing && (
                <SpeakerPanel {...speakerPanelProps} isTranscriptMode={false} />
              )}
              {fileTranscript && !transcribing && (
                <div style={{ marginTop: '16px' }}>
                  <label style={styles.label}>Transcript</label>
                  <textarea
                    style={{ ...styles.textarea, minHeight: '160px', fontFamily: 'monospace', fontSize: '12px' }}
                    value={fileTranscript}
                    onChange={e => setFileTranscript(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Audio file */}
          {inputMode === 'file' && (
            <div>
              <div style={styles.infoAlert}>Upload audio and AssemblyAI will auto-detect speakers. You'll assign each speaker to a client below once transcription finishes — or pick clients above, in Session details.</div>
              {!audioFile ? (
                <div
                  style={styles.uploadZone}
                  onClick={() => document.getElementById('audioFileInput').click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f?.type.startsWith('audio/')) setAudioFile(f); }}
                >
                  <div style={styles.uploadIcon}>🎵</div>
                  <div style={styles.uploadText}>Click or drag to upload audio</div>
                  <div style={styles.uploadSub}>MP3, WAV, M4A, OGG — any length · or drop a .txt/.md file anywhere on this card to paste as transcript</div>
                  <input type="file" id="audioFileInput" accept="audio/*" style={{ display: 'none' }} onChange={e => setAudioFile(e.target.files[0])} />
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                    <span style={styles.fileTag}>{audioFile.name}</span>
                    <button style={styles.ghostBtn} onClick={() => { setAudioFile(null); setFileTranscript(''); setSpeakerLabels([]); }}>Remove</button>
                  </div>
                  <audio controls src={URL.createObjectURL(audioFile)} style={styles.audioPlayer} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0 4px' }}>
                    <label style={{ ...styles.label, margin: 0, whiteSpace: 'nowrap' }}>Speakers in recording</label>
                    <input
                      type="number"
                      min="1"
                      value={speakersExpected}
                      placeholder="Auto"
                      onChange={e => setSpeakersExpected(e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ ...styles.input, width: '72px', textAlign: 'center', padding: '6px 8px' }}
                    />
                    <span style={{ fontSize: '12px', color: 'var(--text-soft)' }}>Leave blank to auto-detect — or enter exact count for better accuracy</span>
                  </div>
                  <button style={styles.actionBtn} onClick={() => transcribeFile(audioFile)} disabled={transcribing}>
                    {transcribing ? transcribingStatus : '🎤 Transcribe with speaker detection'}
                  </button>
                  {transcribing && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={styles.progressBar}><div style={styles.progressFill} /></div>
                      <div style={styles.transcribingStatus}>{transcribingStatus}</div>
                    </div>
                  )}
                </div>
              )}
              {speakerLabels.length > 0 && !transcribing && (
                <SpeakerPanel {...speakerPanelProps} isTranscriptMode={false} />
              )}
              {fileTranscript && (
                <div style={{ marginTop: '16px' }}>
                  <label style={styles.label}>Transcript</label>
                  <textarea
                    style={{ ...styles.textarea, minHeight: '200px', fontFamily: 'monospace', fontSize: '12px' }}
                    value={fileTranscript}
                    onChange={e => setFileTranscript(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Add notes instead — merges the old separate "paste transcript" and
              "notes only" tabs, which already bound to this same `transcript`
              state, so this is a UI-only merge. "Detect speakers" and the
              SpeakerPanel appear once there's actual content, same as before. */}
          {(inputMode === 'notes' || inputMode === 'transcript') && (
            <div>
              <div style={styles.infoAlert}>
                Type quick notes, or paste a full transcript from Zoom, Otter.ai, Fireflies.ai, Rev, or any other source — "Detect speakers" will appear once there's enough to work with.
              </div>
              <textarea
                style={{ ...styles.textarea, minHeight: '220px', fontFamily: inputMode === 'transcript' ? 'monospace' : 'inherit', fontSize: '13px' }}
                placeholder="Type session notes, or paste a full transcript here..."
                value={transcript}
                onChange={e => {
                  setTranscript(e.target.value);
                  // Clear speaker state only on large changes (actual new paste)
                  if (Math.abs(e.target.value.length - transcript.length) > 100) {
                    setSpeakerLabels([]);
                    setSpeakerAssignments({});
                    setSpeakerNames({});
                  }
                }}
              />
              {transcript.trim() && (
                <button style={{ ...styles.actionBtn, marginTop: '10px' }} onClick={() => { setInputMode('transcript'); detectSpeakers(); }}>
                  🔍 Detect speakers
                </button>
              )}
              {speakerLabels.length > 0 && (
                <SpeakerPanel {...speakerPanelProps} isTranscriptMode={true} />
              )}
            </div>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── Step 2: Session Focus ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.stepBadge}>2</div>
          <span style={styles.cardTitle}>Prep & notes</span>
        </div>
        <div style={styles.cardBody}>
          {clientGroupOptions.length > 0 && (
            <div style={{ ...styles.formGroup, marginBottom: '12px' }}>
              <label style={styles.label}>Group membership (optional)</label>
              <select
                style={styles.input}
                value={groupContextId}
                onChange={e => setGroupContextId(e.target.value)}
              >
                <option value="">— Not related to a group —</option>
                {clientGroupOptions.map(g => <option key={g.id} value={g.id}>{selectedClients[0]?.name} is currently in: {g.name}</option>)}
              </select>
              <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '4px' }}>
                Tags this 1:1 as happening while they're a member of that group — noted on their own history, and any real breakthrough here can show up as an individually-attributed highlight on the group's page. It won't be treated as something the whole group experienced.
              </div>
            </div>
          )}

          {selectedClients.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <button
                style={styles.prepBtn}
                onClick={async () => {
                  if (prepData) { setPrepOpen(o => !o); return; }
                  setPrepLoading(true);
                  try {
                    const res = await generateSessionPrep({
                      client_ids: selectedClientIds,
                      group_id: selectedGroupId || null
                    });
                    setPrepData(res.data || res);
                    setPrepOpen(true);
                  } catch {
                    showToast('Could not generate prep brief.');
                  } finally {
                    setPrepLoading(false);
                  }
                }}
                disabled={prepLoading}
              >
                {prepLoading ? 'Preparing your session brief...' : prepData ? (prepOpen ? '▲ Session prep' : '▼ Session prep') : '✦ Generate session prep'}
              </button>

              {prepData && prepOpen && (
                <div style={styles.prepPanel}>
                  <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginBottom: '4px' }}>
                    Click any item to highlight it as something to bring up — add a quick note for yourself if useful.
                  </div>
                  {[
                    { key: 'recent_themes', label: 'Recent themes' },
                    { key: 'open_threads', label: 'Open threads' },
                    { key: 'suggested_focus_areas', label: 'Suggested focus' },
                    { key: 'opening_questions', label: 'Opening questions' },
                  ].map(({ key, label }) => (
                    prepData[key]?.length > 0 && (
                      <div key={key} style={styles.prepSection}>
                        <div style={styles.prepSectionLabel}>{label}</div>
                        {prepData[key].map((item, i) => {
                          const itemKey = `${key}-${i}`;
                          const highlighted = !!highlightedPrepItems[itemKey];
                          return (
                            <div key={i}>
                              <div
                                style={{ ...styles.prepItem, ...(highlighted ? styles.prepItemHighlighted : {}), cursor: 'pointer' }}
                                onClick={() => setHighlightedPrepItems(prev => ({ ...prev, [itemKey]: !prev[itemKey] }))}
                              >
                                {highlighted ? '★' : '—'} {item}
                              </div>
                              {highlighted && (
                                <input
                                  style={{ ...styles.input, marginBottom: '8px', fontSize: '13px' }}
                                  placeholder="Note to yourself (optional)..."
                                  value={prepItemNotes[itemKey] || ''}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setPrepItemNotes(prev => ({ ...prev, [itemKey]: e.target.value }))}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Notes (optional)</label>
            <textarea
              style={styles.textarea}
              value={sessionModalityNotes}
              onChange={e => setSessionModalityNotes(e.target.value)}
              placeholder="e.g. client is processing a layoff this week, go easy on performance framing..."
            />
            <div style={styles.hint}>Passed to the AI as context for this session's summary.</div>
          </div>
        </div>
      </div>

      {/* ── Step 3: Generate Summary ── */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.stepBadge}>3</div>
          <span style={styles.cardTitle}>Generate AI summary</span>
        </div>
        <div style={styles.cardBody}>
          {!generating && !summary && (
            <button style={styles.generateBtn} onClick={handleGenerateSummary}>↻ Generate session summary</button>
          )}
          {generating && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={styles.generatingText}>Generating tailored summary...</div>
              <div style={styles.progressBar}><div style={styles.progressFill} /></div>
            </div>
          )}
          {summary && (
            <div>
              <div style={styles.successAlert}>✓ Summary generated</div>
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'breakthroughs', label: 'Breakthroughs & insights' },
                { key: 'challenges', label: 'Challenges' },
                { key: 'clientGrowth', label: 'Client growth' },
                { key: 'clinicalObservations', label: 'Coaching observations' },
                { key: 'coachRecommendations', label: 'Next session recommendations' },
              ].map(section => summary[section.key] && (
                <div key={section.key} style={styles.summarySection}>
                  <div style={styles.summarySectionTitle}>{section.label}</div>
                  <div style={styles.summaryText}>{summary[section.key]}</div>
                </div>
              ))}
              {summary.keyThemes?.length > 0 && (
                <div style={styles.summarySection}>
                  <div style={styles.summarySectionTitle}>Key themes</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {summary.keyThemes.map((t, i) => <span key={i} style={styles.themeTag}>{t}</span>)}
                  </div>
                </div>
              )}
              {summary.actionItems?.length > 0 && (
                <div style={styles.summarySection}>
                  <div style={styles.summarySectionTitle}>Action items</div>
                  <ul style={styles.summaryList}>{summary.actionItems.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
              )}
              {summary.nextSessionPrompts?.length > 0 && (
                <div style={styles.summarySection}>
                  <div style={styles.summarySectionTitle}>Opening prompts for next session</div>
                  {summary.nextSessionPrompts.map((p, i) => <div key={i} style={styles.promptCard}>{p}</div>)}
                </div>
              )}
              <button style={{ ...styles.ghostBtn, marginTop: '16px' }} onClick={handleGenerateSummary}>↻ Regenerate</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Step 4: Review & Save ── */}
      {summary && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.stepBadge, background: 'var(--gold-pale)', color: 'var(--gold)' }}>4</div>
            <span style={styles.cardTitle}>Review & save</span>
          </div>
          <div style={styles.cardBody}>

            {summary?.breakthroughQuotes?.length > 0 && (
              <div style={styles.reviewSection}>
                <div style={styles.divider} />
                <div style={styles.reviewSectionTitle}>✦ Breakthrough moments</div>
                <div style={styles.reviewSectionSub}>Star moments worth highlighting — they'll appear on the client profile.</div>
                {summary.breakthroughQuotes.filter((_, i) => !dismissedQuotes.includes(i)).map((bq, i) => (
                  <div key={i} style={styles.breakthroughCard}>
                    <div style={styles.breakthroughQuote}>"{bq.quote}"</div>
                    {bq.context && <div style={styles.breakthroughMeta}>{bq.context}</div>}
                    {bq.significance && <div style={styles.breakthroughSig}>{bq.significance}</div>}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      <button
                        style={{ ...styles.starBtn, ...(starredQuotes.includes(i) ? styles.starBtnActive : {}) }}
                        onClick={() => setStarredQuotes(prev => prev.includes(i) ? prev.filter(j => j !== i) : [...prev, i])}
                      >
                        {starredQuotes.includes(i) ? '★ Starred' : '☆ Star'}
                      </button>
                      <button style={styles.dismissBtn} onClick={() => setDismissedQuotes(prev => [...prev, i])}>Dismiss</button>
                    </div>
                  </div>
                ))}
                <div style={styles.manualQuoteBox}>
                  <div style={styles.manualQuoteLabel}>Add your own moment:</div>
                  <textarea
                    style={{ ...styles.textarea, minHeight: '70px' }}
                    placeholder="A quote or moment that stood out..."
                    value={manualQuote}
                    onChange={e => setManualQuote(e.target.value)}
                  />
                  <button style={{ ...styles.addGoalBtn, marginTop: '8px' }} onClick={() => {
                    if (!manualQuote.trim()) return;
                    setManualQuotes(prev => [...prev, { quote: manualQuote.trim(), context: 'Added by coach', starred: true, manual: true }]);
                    setManualQuote('');
                    showToast('Moment added');
                  }}>Add moment</button>
                </div>
              </div>
            )}

            {summary?.suggestedGoals?.length > 0 && (
              <div style={styles.reviewSection}>
                <div style={styles.divider} />
                <div style={styles.reviewSectionTitle}>◎ Suggested goals</div>
                <div style={styles.reviewSectionSub}>Accept goals to add them to this client's long-term goals.</div>
                {summary.suggestedGoals.map((sg, i) => {
                  const status = suggestedGoalStatuses[i] || 'pending';
                  return (
                    <div key={i} style={styles.suggestedGoalCard}>
                      <div style={styles.suggestedGoalText}>{sg.text}</div>
                      {sg.rationale && <div style={styles.breakthroughMeta}>{sg.rationale}</div>}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                        <button
                          style={{ ...styles.acceptBtn, ...(status === 'accepted' ? styles.acceptBtnActive : {}) }}
                          onClick={() => setSuggestedGoalStatuses({ ...suggestedGoalStatuses, [i]: 'accepted' })}
                        >
                          {status === 'accepted' ? '✓ Accepted' : 'Accept'}
                        </button>
                        <button
                          style={{ ...styles.editGoalBtn, ...(status === 'editing' ? styles.acceptBtnActive : {}) }}
                          onClick={() => { setSuggestedGoalStatuses({ ...suggestedGoalStatuses, [i]: 'editing' }); setEditingGoalTexts({ ...editingGoalTexts, [i]: sg.text }); }}
                        >
                          Edit & accept
                        </button>
                        <button style={styles.dismissBtn} onClick={() => setSuggestedGoalStatuses({ ...suggestedGoalStatuses, [i]: 'dismissed' })}>Dismiss</button>
                      </div>
                      {status === 'editing' && (
                        <input
                          style={{ ...styles.input, marginTop: '10px' }}
                          value={editingGoalTexts[i] || sg.text}
                          onChange={e => setEditingGoalTexts({ ...editingGoalTexts, [i]: e.target.value })}
                          onBlur={() => setSuggestedGoalStatuses({ ...suggestedGoalStatuses, [i]: 'accepted' })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={styles.divider} />

            <div style={styles.formGroup}>
              <label style={styles.label}>Coach reflection</label>
              <textarea
                style={styles.textarea}
                value={postReflection}
                onChange={e => setPostReflection(e.target.value)}
                placeholder="How did this session go? What growth are you noticing?"
              />
            </div>

            <button style={styles.saveBtn} onClick={handleSave} disabled={saving || !selectedOfferingId}>
              {saving ? 'Saving...' : `Save to ${selectedClients.length > 1 ? `${selectedClients.length} profiles` : 'client profile'}`}
            </button>
          </div>
        </div>
      )}

      {/* ── Win Capture Prompt ── */}
      {showWinPrompt && (
        <div style={styles.modalOverlay} onClick={() => { setShowWinPrompt(false); finish(); }}>
          <div style={styles.winPrompt} onClick={e => e.stopPropagation()}>
            <div style={styles.winPromptHeader}>Capture a win ✦</div>
            <div style={styles.winPromptSub}>
              Did anything stand out from this session? Note a client transformation moment — you can use these for your practice marketing.
            </div>
            <textarea
              style={{ ...styles.textarea, minHeight: '80px', marginBottom: '16px' }}
              placeholder="Something shifted for them today..."
              value={winText}
              onChange={e => setWinText(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={styles.saveBtn}
                disabled={savingWin || !winText.trim()}
                onClick={async () => {
                  if (!winText.trim()) return;
                  setSavingWin(true);
                  try {
                    await createWin({ text: winText.trim() });
                    showToast('Win saved');
                  } catch {}
                  setShowWinPrompt(false);
                  finish();
                }}
              >
                {savingWin ? 'Saving...' : 'Save win'}
              </button>
              <button
                style={styles.cancelBtn}
                onClick={() => { setShowWinPrompt(false); finish(); }}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Client Modal ── */}
      {showCreateClientModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateClientModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Create client profile</div>
              <button style={styles.modalClose} onClick={() => setShowCreateClientModal(false)}>×</button>
            </div>
            <div style={{ padding: '0 28px' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name</label>
                <input
                  style={styles.input}
                  value={newClientForm.name || speakerNames[createClientSpeaker] || createClientSpeaker || ''}
                  onChange={e => setNewClientForm({ ...newClientForm, name: e.target.value })}
                  placeholder="Client name"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Session type</label>
                <select style={styles.input} value={newClientForm.session_type} onChange={e => setNewClientForm({ ...newClientForm, session_type: e.target.value })}>
                  <option>Life coaching</option>
                  <option>Business coaching</option>
                  <option>Performance coaching</option>
                  <option>Executive coaching</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowCreateClientModal(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleCreateClient} disabled={creatingClient}>
                {creatingClient ? 'Creating...' : 'Create client'}
              </button>
            </div>
          </div>
        </div>
      )}

      <OfferingFormModal
        open={showOfferingModal}
        onClose={() => setShowOfferingModal(false)}
        onSaved={handleOfferingCreatedInline}
        existingOffering={null}
      />
    </div>
  );
}

const styles = {
  page: { padding: '36px 40px', maxWidth: '820px', fontFamily: "'DM Sans', sans-serif" },
  embeddedPage: { fontFamily: "'DM Sans', sans-serif" },
  embeddedCancelBtn: { background: 'none', border: 'none', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '16px' },
  header: { marginBottom: '28px' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  errorClose: { background: 'none', border: 'none', color: 'var(--red)', fontSize: '18px', cursor: 'pointer' },
  card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', marginBottom: '20px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardHeader: { padding: '16px 24px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: '12px' },
  cardTitle: { fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' },
  cardBody: { padding: '24px' },
  stepBadge: { width: '24px', height: '24px', borderRadius: '50%', background: 'var(--mint)', color: 'var(--sage)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  formGroup: { marginBottom: '20px' },
  formRow: { display: 'flex', gap: '16px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '8px' },
  input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', minHeight: '90px', resize: 'vertical' },
  hint: { fontSize: '12px', color: 'var(--text-faint)', marginTop: '6px' },
  link: { color: 'var(--sage)', textDecoration: 'none' },
  selectedBar: { background: 'var(--mint)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' },
  selectedBarLabel: { fontSize: '11.5px', fontWeight: '600', color: 'var(--sage)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' },
  clientTag: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'var(--sage)', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '12.5px' },
  addGoalBtn: { padding: '10px 16px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', whiteSpace: 'nowrap' },
  orDivider: { display: 'flex', alignItems: 'center', textAlign: 'center', margin: '16px 0', color: 'var(--text-faint)', fontSize: '12px' },
  orDividerText: { flex: 1, position: 'relative' },
  liveNowRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--border)', background: 'white', cursor: 'pointer', marginBottom: '14px' },
  liveNowDot: { width: '10px', height: '10px', borderRadius: '50%', background: 'var(--red)', flexShrink: 0 },
  liveNowTitle: { fontFamily: "'DM Sans', sans-serif", fontSize: '14.5px', fontWeight: '600', color: 'var(--text-dark)' },
  liveNowSub: { fontSize: '12px', color: 'var(--text-soft)', marginTop: '2px' },
  liveNowMic: { fontSize: '18px', color: 'var(--gold)' },
  notesInsteadLink: { display: 'block', margin: '0 auto', background: 'none', border: 'none', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer', padding: '6px 0', textAlign: 'center' },
  recordPanel: { border: '2px solid var(--border)', borderRadius: '12px', padding: '28px 24px', textAlign: 'center', background: 'var(--cream)' },
  recordPanelActive: { borderColor: 'var(--red)', background: 'var(--red-light)' },
  recordTimer: { fontFamily: 'monospace', fontSize: '36px', letterSpacing: '0.05em', marginBottom: '20px' },
  recordControls: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' },
  recordBtn: { padding: '11px 22px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  stopBtn: { padding: '11px 22px', borderRadius: '8px', background: 'var(--red)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  pauseBtn: { padding: '11px 22px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', cursor: 'pointer' },
  recordHint: { fontSize: '12px', color: 'var(--text-faint)' },
  transcribingStatus: { fontSize: '12px', color: 'var(--text-soft)', marginTop: '8px', textAlign: 'center' },
  audioPlayer: { width: '100%', borderRadius: '8px', marginTop: '10px' },
  uploadZone: { border: '2px dashed var(--gold)', borderRadius: '12px', padding: '40px 24px', textAlign: 'center', cursor: 'pointer', background: 'var(--cream)', marginBottom: '14px' },
  uploadIcon: { fontSize: '32px', marginBottom: '10px' },
  uploadText: { fontSize: '15px', color: 'var(--text-dark)', marginBottom: '4px', fontWeight: '600' },
  uploadSub: { fontSize: '13px', color: 'var(--text-soft)' },
  uploadLink: { color: 'var(--gold)', textDecoration: 'underline' },
  uploadHint: { fontSize: '11.5px', color: 'var(--text-faint)', marginTop: '6px' },
  fileTag: { background: 'var(--mint)', color: 'var(--sage)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  actionBtn: { padding: '10px 18px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500', display: 'inline-block', marginTop: '12px' },
  ghostBtn: { padding: '10px 16px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
  progressBar: { height: '3px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden', margin: '10px auto', maxWidth: '300px' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, var(--sage), var(--gold))', borderRadius: '2px', width: '100%', animation: 'pulse 1.5s ease-in-out infinite' },
  infoAlert: { background: 'var(--mint)', color: 'var(--sage)', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', lineHeight: 1.6 },
  successAlert: { background: 'var(--mint)', color: 'var(--sage)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px', fontWeight: '500' },
  generateBtn: { padding: '11px 22px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  generatingText: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '10px' },
  summarySection: { marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-light)' },
  summarySectionTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '15px', color: 'var(--sage)', marginBottom: '8px' },
  summaryText: { fontSize: '14px', lineHeight: 1.75, color: 'var(--text-mid)' },
  summaryList: { paddingLeft: '20px', fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.8 },
  themeTag: { background: 'var(--mint)', color: 'var(--sage)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' },
  promptCard: { background: 'var(--gold-pale)', borderLeft: '3px solid var(--gold)', borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: '8px', fontSize: '13.5px', color: 'var(--text-mid)', fontStyle: 'italic' },
  reviewSection: { marginBottom: '20px' },
  reviewSectionTitle: { fontSize: '15px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '6px' },
  reviewSectionSub: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '14px', lineHeight: 1.5 },
  divider: { borderTop: '1px solid var(--border-light)', margin: '20px 0' },
  breakthroughCard: { background: 'var(--gold-pale)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px' },
  breakthroughQuote: { fontSize: '14px', fontStyle: 'italic', color: 'var(--text-dark)', marginBottom: '6px', lineHeight: 1.6 },
  breakthroughMeta: { fontSize: '12px', color: 'var(--text-soft)', marginBottom: '4px' },
  breakthroughSig: { fontSize: '12.5px', color: 'var(--text-mid)', marginBottom: '6px' },
  starBtn: { padding: '5px 12px', borderRadius: '20px', border: '1px solid var(--gold)', background: 'none', color: 'var(--gold)', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  starBtnActive: { background: 'var(--gold)', color: 'white' },
  dismissBtn: { padding: '5px 12px', borderRadius: '20px', border: '1px solid var(--border)', background: 'none', color: 'var(--text-soft)', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  manualQuoteBox: { marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed var(--border)' },
  manualQuoteLabel: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '8px' },
  suggestedGoalCard: { background: 'var(--mint)', border: '1px solid rgba(92,122,107,0.25)', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px' },
  suggestedGoalText: { fontSize: '14px', fontWeight: '500', color: 'var(--text-dark)', marginBottom: '4px' },
  acceptBtn: { padding: '5px 12px', borderRadius: '20px', border: '1px solid var(--sage)', background: 'none', color: 'var(--sage)', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  acceptBtnActive: { background: 'var(--sage)', color: 'white' },
  editGoalBtn: { padding: '5px 12px', borderRadius: '20px', border: '1px solid var(--border)', background: 'none', color: 'var(--text-soft)', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  saveBtn: { padding: '11px 22px', borderRadius: '8px', background: 'var(--gold)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  cancelBtn: { padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(26,46,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, backdropFilter: 'blur(2px)' },
  modal: { background: 'white', borderRadius: '16px', padding: '0', width: '440px', maxWidth: '95vw', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' },
  modalHeader: { padding: '24px 28px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-light)' },
  modalTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: '400', color: 'var(--text-dark)' },
  modalClose: { background: 'none', border: 'none', fontSize: '24px', color: 'var(--text-faint)', cursor: 'pointer' },
  modalFooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '16px 28px 24px', borderTop: '1px solid var(--border-light)', marginTop: '8px' },
  prepBtn: { padding: '9px 16px', borderRadius: '8px', background: 'none', border: '1px solid var(--gold)', color: 'var(--gold)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500' },
  prepPanel: { background: 'var(--mint)', border: '1px solid var(--border-light)', borderRadius: '10px', padding: '18px 20px', marginTop: '10px' },
  prepSection: { marginBottom: '14px' },
  prepSectionLabel: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-soft)', marginBottom: '6px' },
  prepItem: { fontSize: '13.5px', color: 'var(--text-dark)', lineHeight: 1.6, paddingLeft: '4px', borderRadius: '6px', padding: '4px' },
  prepItemHighlighted: { background: 'var(--gold-pale)', fontWeight: '500' },
  winPrompt: { background: 'white', borderRadius: '16px', padding: '28px', width: '460px', maxWidth: '95vw', boxShadow: 'var(--shadow-lg)' },
  winPromptHeader: { fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '8px' },
  winPromptSub: { fontSize: '14px', color: 'var(--text-soft)', lineHeight: 1.6, marginBottom: '16px' },
};